import { Link } from "react-router-dom";

/**
 * Reusable chip/pill component with consistent styling
 *
 * @param {string} label - Text to display
 * @param {string} to - Optional link destination (renders as Link)
 * @param {string} href - Optional external URL (renders as <a>)
 * @param {function} onClick - Click handler
 * @param {function} onRemove - If provided, shows × and calls this on remove click
 * @param {string} variant - "default" | "primary" | "muted" | "active"
 * @param {string} size - "sm" | "md"
 * @param {string} className - Additional classes
 */
const Chip = ({
  label,
  to,
  href,
  onClick,
  onRemove,
  variant = "default",
  size = "md",
  className = "",
}) => {
  // Base styles - outlined, minimal
  const baseStyles = "inline-flex items-center gap-1.5 border rounded transition-colors";

  // Size variants
  const sizeStyles = {
    sm: "px-2 py-0.5 text-[11px]",
    md: "px-2.5 py-1 text-xs",
  };

  // Color variants - all outlined style
  const variantStyles = {
    default: "border-black/15 text-black/50 hover:border-primary hover:text-primary",
    primary: "border-primary/30 text-primary hover:border-primary hover:bg-primary/5",
    muted: "border-black/10 text-black/40 hover:border-black/20 hover:text-black/60",
    active: "border-primary text-primary bg-primary/5",
  };

  const combinedStyles = `${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${className}`;

  const content = (
    <>
      {label}
      {onRemove && (
        <span
          className="text-sm opacity-40 hover:opacity-100 cursor-pointer"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
        >
          ×
        </span>
      )}
    </>
  );

  // Render as Link for internal navigation
  if (to) {
    return (
      <Link to={to} className={`${combinedStyles} cursor-pointer`}>
        {content}
      </Link>
    );
  }

  // Render as <a> for external links
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${combinedStyles} cursor-pointer`}
      >
        {content}
      </a>
    );
  }

  // Render as button/span for click handlers or static chips
  if (onClick || onRemove) {
    return (
      <span className={`${combinedStyles} cursor-pointer`} onClick={onClick}>
        {content}
      </span>
    );
  }

  // Static chip
  return <span className={combinedStyles}>{content}</span>;
};

export default Chip;
